import { motion } from 'framer-motion';
import { useTranslation } from 'next-i18next';

import { ACCENT_BLUE, ACCENT_CYAN, EASE_OUT, NOPING_GREEN } from '../constants';

const ROUTES = [
  { city: 'Chicago', latency: '18ms', best: false },
  { city: 'Dallas', latency: '12ms', best: false },
  { city: 'Denver', latency: '22ms', best: false },
  { city: 'Atlanta', latency: '15ms', best: false },
  { city: 'Seattle', latency: '8ms', best: true },
];

export function MulticonnectionViz() {
  const { t } = useTranslation(['components/trial/lp/v1b']);

  return (
    <div className='space-y-8'>
      <div className='flex items-center justify-between gap-4 py-4 md:gap-6'>
        <motion.div className='flex shrink-0 flex-col items-center gap-2' initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, ease: EASE_OUT }}>
          <div className='flex size-14 items-center justify-center rounded-xl md:size-16' style={{ border: `2px solid ${ACCENT_CYAN}60`, backgroundColor: `${ACCENT_CYAN}08` }}>
            <svg className='size-7 md:size-8' fill='none' viewBox='0 0 24 24' stroke={ACCENT_CYAN} strokeWidth={1.5}>
              <path strokeLinecap='round' strokeLinejoin='round' d='M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25A2.25 2.25 0 015.25 3h13.5A2.25 2.25 0 0121 5.25z' />
            </svg>
          </div>
          <span className='font-mono text-[10px] font-semibold md:text-xs' style={{ color: ACCENT_CYAN }}>
            {t('multiconnection.you')}
          </span>
        </motion.div>

        <div className='flex min-w-0 flex-1 flex-col gap-2 md:gap-3'>
          {ROUTES.map((route, i) => {
            const textColor = route.best ? NOPING_GREEN : `${ACCENT_BLUE}90`;

            return (
              <motion.div key={route.city} className='flex items-center gap-2 md:gap-3' initial={{ opacity: 0, scaleX: 0.3 }} whileInView={{ opacity: 1, scaleX: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.08, duration: 0.4, ease: EASE_OUT }} style={{ originX: 0 }}>
                <div className='relative flex h-8 flex-1 items-center md:h-9'>
                  <div className='absolute inset-0 rounded-lg' style={{ border: `1px solid ${route.best ? NOPING_GREEN : ACCENT_BLUE}${route.best ? '50' : '20'}`, backgroundColor: route.best ? `${NOPING_GREEN}08` : `${ACCENT_BLUE}04` }} />
                  <div className='absolute inset-0 overflow-hidden rounded-lg'>
                    <div className='mc-packet absolute top-1/2 h-1 w-6 -translate-y-1/2 rounded-full md:w-8' style={{ backgroundColor: route.best ? NOPING_GREEN : ACCENT_BLUE, opacity: route.best ? 0.6 : 0.2, animationDuration: `${1.5 + i * 0.3}s` }} />
                  </div>
                  <div className='relative z-10 flex w-full items-center justify-between px-3 md:px-4'>
                    <span className='truncate font-mono text-[10px] font-medium md:text-xs' style={{ color: textColor }}>
                      {route.city}
                    </span>
                    <span className='ms-2 shrink-0 font-mono text-[10px] font-bold md:text-xs' style={{ color: route.best ? NOPING_GREEN : `${ACCENT_BLUE}70` }}>
                      {route.latency}
                      {route.best && (
                        <span className='ms-1.5 rounded-full px-1.5 py-0.5 text-[8px] font-semibold uppercase md:text-[9px]' style={{ backgroundColor: `${NOPING_GREEN}15`, color: NOPING_GREEN }}>
                          {t('multiconnection.best')}
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.div className='flex shrink-0 flex-col items-center gap-2' initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.4, duration: 0.4, ease: EASE_OUT }}>
          <div className='flex size-14 items-center justify-center rounded-xl md:size-16' style={{ border: `2px solid ${NOPING_GREEN}60`, backgroundColor: `${NOPING_GREEN}08` }}>
            <svg className='size-7 md:size-8' fill='none' viewBox='0 0 24 24' stroke={NOPING_GREEN} strokeWidth={1.5}>
              <path strokeLinecap='round' strokeLinejoin='round' d='M5.25 14.25h13.5m-13.5 0a3 3 0 01-3-3m3 3a3 3 0 100 6h13.5a3 3 0 100-6m-16.5-3a3 3 0 013-3h13.5a3 3 0 013 3m-19.5 0a4.5 4.5 0 01.9-2.7L5.737 5.1a3.375 3.375 0 012.7-1.35h7.126c1.062 0 2.062.5 2.7 1.35l2.587 3.45a4.5 4.5 0 01.9 2.7m0 0a3 3 0 01-3 3m0 3h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008zm-3 6h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008z' />
            </svg>
          </div>
          <span className='font-mono text-[10px] font-semibold md:text-xs' style={{ color: NOPING_GREEN }}>
            {t('multiconnection.game_server')}
          </span>
        </motion.div>
      </div>

      <motion.div className='flex flex-wrap items-center justify-center gap-4' initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.6, duration: 0.4, ease: EASE_OUT }}>
        <div className='inline-flex items-center gap-2 rounded-full px-4 py-2 font-mono text-xs' style={{ color: ACCENT_BLUE, backgroundColor: `${ACCENT_BLUE}10`, border: `1px solid ${ACCENT_BLUE}20` }}>
          <span className='size-1.5 rounded-full' style={{ backgroundColor: ACCENT_BLUE }} />
          {t('multiconnection.combinations')}
        </div>
        <div className='inline-flex items-center gap-2 rounded-full px-4 py-2 font-mono text-xs' style={{ color: NOPING_GREEN, backgroundColor: `${NOPING_GREEN}10`, border: `1px solid ${NOPING_GREEN}20` }}>
          <span className='size-1.5 animate-pulse rounded-full' style={{ backgroundColor: NOPING_GREEN }} />
          {t('multiconnection.auto_selected')}
        </div>
      </motion.div>

      <style>{`
        .mc-packet {
          animation: mcPacketMove linear infinite;
        }
        @keyframes mcPacketMove {
          0% { left: -8%; }
          100% { left: 100%; }
        }
      `}</style>
    </div>
  );
}
